import { useRef, useState, type MouseEvent } from "react";
import Box from "@mui/material/Box";
import Popper from "@mui/material/Popper";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { CONCEPT_VALUE_ALL_OPTION, type TaggingPlanRow } from "../constants";

/** Matches Add page Reference Dimension fallback when display name is missing. */
const REFERENCE_DOC_TYPE_DISPLAY = "DocType";

export type ReferenceItemDetail = {
  label: string;
  object: string;
  dimension: string;
  value: string;
};

function formatDimensionValues(
  allValues: boolean,
  values: ReadonlyArray<{ code: string; name?: string }>,
): string {
  if (allValues) return CONCEPT_VALUE_ALL_OPTION.name;
  return values
    .map((item) => item.name?.trim() || item.code)
    .filter(Boolean)
    .join(", ");
}

/**
 * Build homepage Reference Items detail cards (R1, R2, …) from a plan row.
 * Each `referenceDimensions` entry becomes one Rn; object-only / legacy subtype
 * falls back to a single R1.
 */
export function buildReferenceItemDetails(
  row: TaggingPlanRow,
): ReferenceItemDetail[] {
  const objectName = row.objectTypeName?.trim() || "";
  if (!objectName) return [];

  if (row.referenceDimensions?.length > 0) {
    return row.referenceDimensions.map((dim, index) => ({
      label: `R${index + 1}`,
      object: objectName,
      dimension:
        dim.conceptDisplayName?.trim() || dim.conceptCode.trim() || "",
      value: formatDimensionValues(dim.allValues, dim.values),
    }));
  }

  const subTypeName = row.objectSubTypeName?.trim() || "";
  if (!subTypeName) {
    // Object-only selection — still surface as R1 with Object filled.
    return [
      {
        label: "R1",
        object: objectName,
        dimension: "",
        value: "",
      },
    ];
  }

  const nameParts = subTypeName
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  const codeParts = (row.objectSubType || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  const isAll =
    (codeParts.length === 1 &&
      codeParts[0]!.toLowerCase() ===
        CONCEPT_VALUE_ALL_OPTION.code.toLowerCase()) ||
    nameParts.some(
      (name) =>
        name.toLowerCase() === CONCEPT_VALUE_ALL_OPTION.name.toLowerCase(),
    );
  const dimensionLabel =
    row.referenceConceptDisplayName?.trim() ||
    row.referenceConceptCode?.trim() ||
    REFERENCE_DOC_TYPE_DISPLAY;

  return [
    {
      label: "R1",
      object: objectName,
      dimension: dimensionLabel,
      value: isAll
        ? CONCEPT_VALUE_ALL_OPTION.name
        : nameParts.join(", "),
    },
  ];
}

/** Cell trigger text: `R1, R2`. */
export function formatReferenceItemsRnLabel(
  details: readonly ReferenceItemDetail[],
): string {
  return details.map((item) => item.label).join(", ");
}

type ReferenceItemsRnCellProps = Readonly<{
  details: readonly ReferenceItemDetail[];
  /**
   * Popover title noun. Classification / OTO Reference use "Reference";
   * OTO Impact Items use "Impact".
   */
  itemKindLabel?: string;
}>;

/**
 * Shared dashed `R1, R2` / `I1, I2` trigger + hover popover (Classification + OTO).
 */
export function ReferenceItemsRnCell({
  details,
  itemKindLabel = "Reference",
}: ReferenceItemsRnCellProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const label = formatReferenceItemsRnLabel(details);
  const open = Boolean(anchorEl) && details.length > 0;

  const clearCloseTimer = () => {
    if (closeTimerRef.current != null) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    if (details.length === 0) return;
    clearCloseTimer();
    setAnchorEl(event.currentTarget);
  };

  const handleCloseSoon = () => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      setAnchorEl(null);
      closeTimerRef.current = null;
    }, 120);
  };

  if (!label) {
    return (
      <Typography sx={{ color: "#8c8c8c", fontSize: "14px" }}>-</Typography>
    );
  }

  const title = `${details.length} ${itemKindLabel} ${
    details.length === 1 ? "Item" : "Items"
  }`;

  return (
    <>
      <Box
        component="span"
        onMouseEnter={handleOpen}
        onMouseLeave={handleCloseSoon}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          maxWidth: "100%",
          minWidth: 0,
          cursor: "default",
          color: "#364153",
          fontSize: "14px",
          lineHeight: "16px",
          borderBottom: "1px dashed #99A1AF",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          "&:hover": {
            color: "#295EF1",
            borderBottomColor: "#295EF1",
          },
        }}
      >
        {label}
      </Box>
      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        modifiers={[
          { name: "offset", options: { offset: [0, 6] } },
          { name: "preventOverflow", options: { padding: 8 } },
        ]}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
      >
        <Paper
          elevation={0}
          onMouseEnter={clearCloseTimer}
          onMouseLeave={handleCloseSoon}
          sx={{
            width: 280,
            borderRadius: "8px",
            border: "1px solid #E5E7EB",
            boxShadow: "0 8px 24px rgba(25, 37, 50, 0.16)",
            p: "12px 14px",
          }}
        >
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 600,
              color: "#101828",
              mb: "10px",
            }}
          >
            {title}
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {details.map((item) => (
              <Box
                key={item.label}
                sx={{
                  backgroundColor: "#F9FAFB",
                  borderRadius: "4px",
                  p: "8px 10px",
                  display: "flex",
                  gap: "10px",
                }}
              >
                <Box
                  component="span"
                  sx={{
                    flex: "none",
                    fontSize: "11px",
                    fontWeight: 600,
                    color: "#295EF1",
                    backgroundColor: "#E7ECFC",
                    borderRadius: "3px",
                    px: "6px",
                    height: "18px",
                    lineHeight: "18px",
                  }}
                >
                  {item.label}
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    display: "grid",
                    gridTemplateColumns: "64px minmax(0, 1fr)",
                    rowGap: "4px",
                    columnGap: "8px",
                    fontSize: "12px",
                  }}
                >
                  <Box component="span" sx={{ color: "#6A7282" }}>
                    Object
                  </Box>
                  <Box component="span" sx={{ color: "#0D0D0D" }}>
                    {item.object || "—"}
                  </Box>
                  <Box component="span" sx={{ color: "#6A7282" }}>
                    Dimension
                  </Box>
                  <Box component="span" sx={{ color: "#0D0D0D" }}>
                    {item.dimension || "—"}
                  </Box>
                  <Box component="span" sx={{ color: "#6A7282" }}>
                    Value
                  </Box>
                  <Box
                    component="span"
                    sx={{ color: "#0D0D0D", fontWeight: 500 }}
                  >
                    {item.value || "—"}
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Paper>
      </Popper>
    </>
  );
}

type ReferenceItemsCellProps = Readonly<{
  row: TaggingPlanRow;
}>;

/**
 * Classification Plan homepage Reference Items cell — dashed `R1, R2` trigger
 * with hover popover (Object / Dimension / Value per Rn).
 */
export default function ReferenceItemsCell({ row }: ReferenceItemsCellProps) {
  return <ReferenceItemsRnCell details={buildReferenceItemDetails(row)} />;
}
